import { ManagedLeadRequestValuesResult } from '../../services/api/managed-leads/managedLeads';
import {
  describeManagedLeadWriteFailure,
  isManagedLeadNoteRetryable,
  ManagedLeadWriteAct,
  ManagedLeadWriteFailure,
  ManagedLeadWriteOutcome,
} from './managedLeadFailures';
import { getTassoQuintoErrorCode } from '../../services/api/tasso-quinto';

/**
 * What a correction that did not simply succeed means for the modal: the
 * refusals only this write path can receive, and the announcement of a
 * correction whose figures landed while its automatic Nota did not.
 */

export const MANAGED_LEAD_REQUEST_VALUES_UNCHANGED_MESSAGE =
  'Nessuna modifica da salvare: i valori sono già questi.';

export const MANAGED_LEAD_REQUEST_VALUES_SAVED_MESSAGE =
  'Valori richiesti corretti.';

export const MANAGED_LEAD_REQUEST_VALUES_FAILED_MESSAGE =
  'Non è stato possibile salvare la correzione. I dati inseriti sono stati conservati.';

/**
 * How the correction names itself in the failures it shares with the update.
 * Nothing but the noun changes: a rejected payload, a refused scope, a vanished
 * lead and an expired session mean the same on both write paths, so they are
 * translated once rather than twice with two wordings.
 */
const CORRECTION_ACT: ManagedLeadWriteAct = {
  failed: MANAGED_LEAD_REQUEST_VALUES_FAILED_MESSAGE,
  invalid: 'La correzione non è valida',
  // The correction asks for the same authorisation as an update, so a refused
  // scope is the same refusal the operator would read from the update form.
  unauthorized: 'Non sei autorizzato ad aggiornare questa lead.',
  resend: 'Riprova per inviare questa correzione.',
};

/**
 * Translates a failed correction into what the operator sees and what the modal
 * does next, reacting to the stable envelope `code` and never to its message.
 *
 * The two codes answered here are the availability refusals: the pencil mirrors
 * the server rule, but a colleague can close the pratica or ask for its Drop
 * while the modal is open, and the server - which stays authoritative - then
 * refuses a save the pencil had allowed. Neither is retryable, and both leave
 * the page describing a lead that has moved on, so both re-read the detail.
 */
export const describeManagedLeadRequestValuesFailure = (
  error: unknown,
  /**
   * How the operator hears each figure the payload carried. The modal shows
   * every one of them, so a rejected figure always has an input of its own to
   * sit under and nothing has to be named in the alert alone.
   */
  valueLabels: Record<string, string> = {}
): ManagedLeadWriteFailure => {
  const shared = describeManagedLeadWriteFailure(error, CORRECTION_ACT, {
    form: valueLabels,
    other: {},
  });

  switch (getTassoQuintoErrorCode(error)) {
    case 'deal_not_correctable':
      return {
        ...shared,
        message:
          'Questa lead non è più correggibile nella fase corrente. Il dettaglio è stato ricaricato.',
        retryable: false,
        refetchesDetail: true,
      };
    case 'drop_review_pending':
      return {
        ...shared,
        message:
          'È stata richiesta la revisione di un Drop per questa lead: la correzione non è stata salvata. Il dettaglio è stato ricaricato.',
        retryable: false,
        refetchesDetail: true,
      };
    default:
      return shared;
  }
};

/**
 * What a `200` means for the modal: the automatic Nota is the only place the
 * previous figure survives, so figures written without it are an outstanding
 * step and never a plain success.
 */
export type ManagedLeadRequestValuesOutcome = ManagedLeadWriteOutcome;

const NOTE_NOT_CREATED_MESSAGE =
  'I valori sono stati corretti, ma la nota automatica con i valori precedenti non è stata salvata. Riprova: verrà ritentata soltanto la nota.';

const REPLAYED_MESSAGE =
  'Questa correzione era già stata registrata: non è stata duplicata.';

/** Describes a completed correction. */
export const describeManagedLeadRequestValuesOutcome = (
  result: ManagedLeadRequestValuesResult
): ManagedLeadRequestValuesOutcome => {
  if (result.outcome === 'partial_success') {
    return {
      severity: 'warning',
      message: NOTE_NOT_CREATED_MESSAGE,
      retryable: isManagedLeadNoteRetryable(result.note),
    };
  }

  const settled = { severity: 'success', retryable: false } as const;
  if (result.replayed) return { ...settled, message: REPLAYED_MESSAGE };

  // The server found nothing to write: another operator had already corrected
  // the same figures, so nothing changed and no Nota was created.
  return {
    ...settled,
    message: result.valuesUpdated.length
      ? MANAGED_LEAD_REQUEST_VALUES_SAVED_MESSAGE
      : MANAGED_LEAD_REQUEST_VALUES_UNCHANGED_MESSAGE,
  };
};
